/**
 * buildDescriptorJSON.js
 *
 * Single source of truth for survey descriptor JSON generation.
 * Both cloud save and local export use this module.
 *
 * This module maps every designer node property to its kernel-expected
 * JSON field name, gates fields correctly (e.g. variable_name only when
 * collectsInput is true), and ensures nothing authored in the designer
 * is silently dropped.
 *
 * Field mapping reference:
 *   Designer (node.data)        →  JSON output             →  Kernel reads
 *   ─────────────────────────────────────────────────────────────────────
 *   collectsInput               →  collectsInput            →  collectsInput (boolean)
 *   variable / variable_name    →  variable_name            →  variable_name (gated on collectsInput)
 *   text / question_text        →  question_text            →  question_text
 *   type / question_type        →  question_type            →  question_type
 *   id / display_id             →  display_id, legacy_label →  display_id
 *   required                    →  required                 →  required
 *   category                    →  category                 →  category
 *   choices                     →  choices                  →  choices / options
 *   minValue                    →  min_value                →  min_value
 *   maxValue                    →  max_value                →  max_value
 *   context                     →  context                  →  context
 *   helpText                    →  help_text                →  help_text
 *   clarificationPolicy         →  clarificationPolicy      →  clarificationPolicy
 *   question_mode               →  question_mode            →  question_mode
 *   intent_brief                →  intent_brief             →  intent_brief
 *   conversational              →  conversational           →  conversational
 *   gravity                     →  gravity                  →  gravity
 *   spokenResponse              →  spokenResponse           →  spokenResponse
 *   show (media)                →  question_type            →  display_stimulus / dismiss_stimulus
 *   stimulus_id                 →  stimulus_id              →  stimulus_id
 *   media                       →  media                    →  media
 *   alt                         →  alt                      →  alt
 *   branches (decision)         →  branches                 →  branches
 */

const { backfillVariableNames } = require('./variableNames');
const { compileChannelDeliveryPlan, canonicalChannels } = require('./channelDeliveryPlan');

// ────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────

/**
 * Labels-as-expressions (ruling 2026-08-02): the branch label is the single
 * authored routing construct — it is both what the canvas displays and the
 * expression the kernel's decision model evaluates ("Not at all",
 * "!= Not at all", "Other"). Structured predicates are no longer emitted.
 *
 * Legacy migration: surveys authored before this ruling carry a condition
 * expression on the edge ("Q2vigorous != \"Not at all\"") and often only a
 * default label. When the label is missing, synthesize an expression-style
 * label from the legacy condition so the branch keeps its routing meaning.
 */
function legacyConditionToLabel(conditionStr) {
  const cond = String(conditionStr || '').trim();
  if (!cond) return '';
  const m = cond.match(/^(\w+)\s*(==|!=|>=|>|<=|<)\s*(.+)$/);
  if (!m) return cond; // compound/unrecognized: carry the raw expression as the label
  const [, , op, rawVal] = m;
  const value = rawVal.trim().replace(/^"(.*)"$/, '$1');
  return op === '==' ? value : `${op} ${value}`;
}

/**
 * Determine collectsInput for a node.
 * Explicit value on node.data wins; otherwise infer from node type.
 * Non-input structural types always return false regardless of data.
 */
function resolveCollectsInput(node) {
  const d = node.data || {};
  const nodeType = node.type;

  // Structural types are NEVER input-collecting
  if (nodeType === 'start' || nodeType === 'end' || nodeType === 'decision' || nodeType === 'media'
    || nodeType === 'pleasant') {
    return false;
  }

  // Explicit designer flag (set by NodeEditor checkbox)
  if (d.collectsInput !== undefined) return !!d.collectsInput;
  if (node.collectsInput !== undefined) return !!node.collectsInput;

  // Default: question nodes collect input
  return true;
}

/**
 * Resolve the question_type string for export.
 */
function resolveQuestionType(node) {
  const d = node.data || {};
  if (node.type === 'start') return 'introduction';
  if (node.type === 'end') return 'completion';
  if (node.type === 'decision') return 'decision';
  if (node.type === 'media') return d.show !== false ? 'display_stimulus' : 'dismiss_stimulus';
  if (node.type === 'pleasant') return 'pleasantry';
  return d.type || d.question_type || 'text';
}

/**
 * Conditionally include a field: only add to object if value is
 * not undefined, not null, and not empty string.
 */
function includeIf(obj, key, value) {
  if (value !== undefined && value !== null && value !== '') {
    obj[key] = value;
  }
}

/**
 * Conditionally include an object field if it has properties.
 */
function includeObjIf(obj, key, value) {
  if (value && typeof value === 'object' && Object.keys(value).length > 0) {
    obj[key] = value;
  }
}

/**
 * Conditionally include an array field if non-empty.
 */
function includeArrIf(obj, key, value) {
  if (Array.isArray(value) && value.length > 0) {
    obj[key] = value;
  }
}

// ────────────────────────────────────────────────────────────────────────
// Build a single question entry
// ────────────────────────────────────────────────────────────────────────

function buildQuestionEntry(node, edges) {
  const d = node.data || {};
  const collectsInput = resolveCollectsInput(node);
  const questionType = resolveQuestionType(node);

  // ── Core fields (always present) ──────────────────────────────────
  const question = {
    question_id: node.id,
    question_text: d.text || d.question_text || '',
    question_type: questionType,
    collectsInput: collectsInput,
    required: d.required !== false,
    category: d.category || 'general',
    display_id: d.id || d.display_id || node.id,
    legacy_label: d.id || ''
  };

  // ── Variable name: ONLY when collectsInput is true ────────────────
  if (collectsInput) {
    const varName = d.variable || d.variable_name || null;
    question.variable_name = varName;
  } else {
    question.variable_name = null;
  }

  // ── Question mode ─────────────────────────────────────────────────
  // Include for all question-type nodes; kernel uses this for verbatim vs intent routing
  if (node.type === 'question' || node.type === 'start' || node.type === 'end') {
    question.question_mode = d.question_mode || 'verbatim';
    includeIf(question, 'intent_brief', d.intent_brief);
  }

  // ── Spoken response (used by FSM for TTS; falls back to question_text) ──
  includeIf(question, 'spokenResponse', d.spokenResponse || d.spokenText);

  // ── Type-specific options ─────────────────────────────────────────
  if (node.type === 'question') {
    includeArrIf(question, 'choices', d.choices);
    // Ordinal semantic-classification response categories for text questions (WP-D1)
    if (d.type === 'text') includeArrIf(question, 'response_categories', d.responseCategories);
    includeIf(question, 'min_value', d.minValue);
    includeIf(question, 'max_value', d.maxValue);
    includeIf(question, 'context', d.context);
    includeIf(question, 'help_text', d.helpText);
    question.clarificationPolicy = d.clarificationPolicy || 'auto';

    // ── File upload settings (External Event Node class) ────────────
    // See docs/external-event-nodes.md: the file node is an external-event
    // node, not a question subtype. node_class/event_kind/awaits/event_spec
    // are the phase-2 declaration; the flat legacy fields are kept alongside
    // for older kernel revisions. collectsInput is forced true here because
    // blocking event nodes pause the survey — the class decides this, not a
    // designer checkbox (the checkbox is hidden for file nodes).
    if (d.type === 'file') {
      question.node_class = 'external_event';
      question.event_kind = 'file_upload';
      question.awaits = 'result';
      question.collectsInput = true;
      const constraints = {};
      if (Array.isArray(d.accept) && d.accept.length) constraints.accept = d.accept;
      if (d.maxSizeMB !== undefined && d.maxSizeMB !== null && d.maxSizeMB !== '') constraints.maxSizeMB = Number(d.maxSizeMB);
      constraints.required = d.required !== false;
      question.event_spec = { constraints };
      // Result slot: the variable the system fills with the stored file
      // reference. Optional; not gated on the (hidden) collectsInput checkbox.
      const fileVar = (d.variable || d.variable_name || '').trim();
      question.variable_name = fileVar || null;
      // Legacy flat fields for back-compat
      includeArrIf(question, 'accept', d.accept);
      includeIf(question, 'maxSizeMB', d.maxSizeMB);
      includeIf(question, 'storage', d.storage);
      if (d.quarantine !== undefined) question.quarantine = d.quarantine;
    }
  }

  // ── Personality / AI behaviour (conversational, gravity) ────────────
  // These are authored in the Personality tab and Advanced dialogs.
  // Include for question, start, and end nodes (kernel reads them from metadata).
  if (node.type === 'question' || node.type === 'start' || node.type === 'end') {
    includeObjIf(question, 'conversational', d.conversational);
    includeObjIf(question, 'gravity', d.gravity);
  }

  // ── Decision node ─────────────────────────────────────────────────
  if (node.type === 'decision') {
    question.question_text = question.question_text || '(decision)';
    // Synthesize branches from outgoing React Flow edges. The label is the
    // routing expression (labels-as-expressions); no predicate is emitted.
    const outEdges = edges.filter(e => e.source === node.id);
    question.branches = outEdges.map((e, i) => ({
      branch_id: `branch_${i + 1}`,
      label: (e.data?.label || '').trim()
        || legacyConditionToLabel(e.data?.condition)
        || `Branch ${i + 1}`,
      commentary_text: e.data?.description || '',
      target: e.target
    }));
  }

  // ── Media node (External Event Node class, awaits 'none') ─────────
  if (node.type === 'media') {
    question.question_text = question.question_text || '(media)';
    question.node_class = 'external_event';
    question.event_kind = 'media';
    question.awaits = 'none';
    question.stimulus_id = d.stimulus_id || '';
    question.media = d.media || {};
    question.alt = d.alt || '';
  }

  // ── Pleasant node (non-input, line generated by the LLM at runtime) ─
  if (node.type === 'pleasant') {
    question.question_text = '';
    question.length = d.length || 'one_line';
    question.scope = d.scope || 'in_context';
    question.flavor = d.flavor || 'joke';
  }

  // ── Package provenance (WP-2 §3.2) ────────────────────────────────
  // Stamped by expandPackageNodes on every spliced question. The kernel ignores
  // unknown fields; these enable Explorer grouping and debugging by package.
  includeIf(question, 'package_id', d.package_id);
  includeIf(question, 'package_version', d.package_version);
  includeIf(question, 'package_instance', d.package_instance);

  return question;
}

// ────────────────────────────────────────────────────────────────────────
// Build survey_descriptor header
// ────────────────────────────────────────────────────────────────────────

function buildSurveyDescriptorHeader(surveyData, questions) {
  const targetChannels = canonicalChannels(surveyData.target_channels || ['browser']);
  const channelDeliveryPlan = compileChannelDeliveryPlan({
    questions,
    selectedChannels: targetChannels,
    overrides: surveyData.channel_delivery_overrides || {},
    reviewedAt: surveyData.channel_profile_reviewed_at || null,
    reviewedBy: surveyData.channel_profile_reviewed_by || null,
    reviewSignature: surveyData.channel_profile_review_signature || null,
  });
  return {
    survey_name: surveyData.title || 'Untitled Survey',
    survey_purpose: surveyData.description || '',
    estimated_time: surveyData.estimatedDuration || '',
    author: surveyData.author || '',
    version: surveyData.version || '1.0',
    category: surveyData.category || 'general',
    voice_settings: { sam_voice: surveyData.googleVoice || '' },
    language: surveyData.language || 'en',
    target_channels: targetChannels.length ? targetChannels : ['browser'],
    channel_delivery_plan: channelDeliveryPlan,
    participant_rules: Array.isArray(surveyData.participant_rules) ? surveyData.participant_rules : [],
    captureAudio: surveyData.settings?.captureAudio || false,
    includeConfidence: surveyData.settings?.includeConfidence || false,
    // Pacing: survey-wide anxiety thresholds in ms. The kernel escalates its
    // redirect strategy as a respondent's stall crosses each one. Omitted when
    // unset so the kernel keeps its own defaults.
    ...(surveyData.settings?.anxietyThresholds
      ? { anxiety_thresholds: surveyData.settings.anxietyThresholds }
      : {}),
    // Answers scoring below this are stamped low_confidence on the response row.
    ...(surveyData.settings?.confidenceThreshold !== undefined
      ? { required_confidence: surveyData.settings.confidenceThreshold }
      : {})
  };
}

// ────────────────────────────────────────────────────────────────────────
// Public API
// ────────────────────────────────────────────────────────────────────────

/**
 * Build the complete descriptor JSON from designer state.
 *
 * @param {Object} params
 * @param {Array}  params.sortedQuestionNodes - BFS-sorted nodes (question, start, end, decision, media)
 * @param {Array}  params.edges               - React Flow edges (for branch synthesis)
 * @param {Object} params.surveyData          - Survey-level metadata (title, author, etc.)
 * @param {Array}  params.flowSorted          - Normalized and sorted flow array
 * @returns {Object} Complete descriptor ready for cloud save or file export
 */
function buildDescriptorJSON({ sortedQuestionNodes, edges, surveyData, flowSorted }) {
  const questions = sortedQuestionNodes.map(node => buildQuestionEntry(node, edges));
  // Last line of defence for variable names. Both Export and Save to Cloud come
  // through here, so a question whose name was cleared by accident gets one
  // back before the descriptor can reach the kernel — which would otherwise
  // refuse to load the whole survey (VARIABLE_REQUIRED). Silent on purpose.
  backfillVariableNames(questions);
  return {
    survey_descriptor: buildSurveyDescriptorHeader(surveyData, questions),
    questions,
    flow: flowSorted
  };
}

/**
 * Derive the media-asset ref set from a compiled descriptor (WP-2 §3.8).
 *
 * Every displayed stimulus (question_type === 'display_stimulus') that points
 * at a real asset yields one ref. Deriving from the descriptor — rather than
 * walking raw nodes — is what lets refs see through embedded packages: expanded
 * package media appears here as ordinary display_stimulus questions with
 * correctly namespaced ids and their asset_id intact.
 *
 * Behaviour-identical to the legacy raw-node walk for package-free surveys.
 *
 * @param {Object} descriptor - output of buildDescriptorJSON
 * @returns {Array<{question_id, asset_id, stimulus_id}>}
 */
function deriveMediaRefsFromDescriptor(descriptor) {
  const questions = (descriptor && descriptor.questions) || [];
  return questions
    .filter(q => q.question_type === 'display_stimulus' && q.media && q.media.src)
    .map(q => ({
      question_id: q.question_id,
      asset_id: q.media.asset_id ?? null,
      stimulus_id: q.stimulus_id || null
    }));
}

module.exports = { buildDescriptorJSON, deriveMediaRefsFromDescriptor };
module.exports.default = buildDescriptorJSON;
